import { authenticatedFastApiAxiosInstance } from "@/lib/data/http";
import { isAxiosError } from "axios";
import { omit } from "lodash";
import type {
  APIResponse,
  Connection,
  ConnectionCreate,
  ConnectionReplace,
  ConnectionUpdate,
} from "@/lib/types";

export type ConnectionOutcome = Pick<
  Connection,
  "editRevision" | "lifecycleRootId" | "state"
> & { connectionId: string };
export type ConnectionEditContext = Pick<
  Connection,
  "editRevision" | "sourceObjectId" | "sourceObjectType"
> & { matterId?: string };

// An exact retry reuses its receipt ID. Changed values are a new intent.
const receipts = new WeakMap<object, Map<string, string>>();
const operationIdFor = (intent: object, payload: object) => {
  const fingerprint = JSON.stringify(payload);
  const intents = receipts.get(intent) ?? new Map<string, string>();
  receipts.set(intent, intents);
  const id = intents.get(fingerprint) ?? crypto.randomUUID();
  intents.set(fingerprint, id);
  return id;
};
// Retain the complete original command after a lost acknowledgement, including
// revisions and peer intent. Cache refreshes must not change an exact retry.
const pendingCommands = new Map<string, object>();
export const isRejectedConnectionOperation = (error: unknown) =>
  isAxiosError(error) &&
  [400, 404, 409, 410, 422].includes(error.response?.status ?? 0);

const submitCommand = async (id: string, command: object) => {
  pendingCommands.set(id, command);
  try {
    const response = await authenticatedFastApiAxiosInstance.post<
      APIResponse<ConnectionOutcome>
    >("v4/connections/operations", command);
    pendingCommands.delete(id);
    return response.data;
  } catch (error) {
    if (isRejectedConnectionOperation(error)) pendingCommands.delete(id);
    throw error;
  }
};

// Replay before consulting the owner cache: an acknowledged removal or
// replacement may already have disappeared from a refreshed owning response.
export const replayPendingConnectionV4 = (operationId?: string) => {
  if (!operationId) return undefined;
  const command = pendingCommands.get(operationId);
  return command ? submitCommand(operationId, command) : undefined;
};

const submit = async (
  intent: object,
  payload: object,
  operationId?: string,
) => {
  const id = operationId ?? operationIdFor(intent, payload);
  const command = pendingCommands.get(id) ?? { ...payload, operationId: id };
  try {
    return await submitCommand(id, command);
  } catch (error) {
    if (isRejectedConnectionOperation(error))
      receipts.get(intent)?.delete(JSON.stringify(payload));
    throw error;
  }
};
const creationValues = (data: ConnectionCreate & { matterId?: string }) => {
  return {
    ...omit(data, ["targetObjectId", "targetObjectType", "operationId"]),
    contactId: data.targetObjectId,
  };
};
const editValues = (connectionId: string, context: ConnectionEditContext) => {
  if (!Number.isInteger(context.editRevision) || context.editRevision < 1)
    throw new Error("Reload the owning object before editing this connection.");
  return {
    connectionId,
    expectedRevision: context.editRevision,
    sourceObjectId: context.sourceObjectId,
    sourceObjectType: context.sourceObjectType,
  };
};
export const createConnectionV4 = (
  data: ConnectionCreate & { matterId?: string },
) =>
  submit(
    data,
    { operation: "create", ...creationValues(data) },
    data.operationId,
  );
export const replaceConnectionV4 = (
  connectionId: string,
  data: ConnectionReplace,
  context: ConnectionEditContext,
) =>
  submit(
    data,
    {
      operation: "replace",
      peerRevisions: data.peerRevisions,
      ...editValues(connectionId, context),
      replacement: creationValues({
        ...data.replacement,
        matterId: context.matterId,
      }),
    },
    data.operationId ?? data.replacement.operationId,
  );
export const updateConnectionV4 = (
  connectionId: string,
  data: ConnectionUpdate,
  context: ConnectionEditContext,
) => {
  const values = omit(data, ["dateModified", "operationId"]);
  return submit(
    data,
    {
      ...values,
      ...editValues(connectionId, context),
      matterId: context.matterId,
    },
    data.operationId,
  );
};
export const deleteConnectionV4 = (
  connection: Connection,
  _matterId?: string,
  operationId?: string,
  peerRevisions?: Record<string, number>,
) =>
  submit(
    connection,
    {
      operation: "remove",
      peerRevisions,
      ...editValues(connection.id, connection),
    },
    operationId,
  );
